import { __decorate, __metadata, __param } from "tslib";
import { Symbols } from '@/symbols';
import { inject, injectable } from 'inversify';
import _ from 'lodash';
import { getDateFormatYMD, getDateFormatYMDHMS } from '@/utils/date';
import { E_AcvAndArr } from '../customer/CustomerService';
let CompanyInfoService = class CompanyInfoService {
    constructor(i18n, customerService, systemService) {
        this.i18n = i18n;
        this.customerService = customerService;
        this.systemService = systemService;
    }
    /**
     * 获取公司基本信息
     */
    getBaseInfo(companyInfo, companyAttributeData) {
        const info = companyInfo?.organization_info;
        const salesforceInfo = companyAttributeData?.company_salesforce;
        let patsnap_category = null;
        if (info?.patsnap_category) {
            patsnap_category = info.patsnap_category;
            const i18nKey = `enum.E_PatentCategory.${patsnap_category}`;
            if (this.i18n.t(i18nKey) !== i18nKey) {
                patsnap_category = this.i18n.t(i18nKey);
            }
        }
        return this.systemService.IsCn
            ? [
                { key: 'patsnap_category', label: this.i18n.t('backoffice.company.patsnap_category'), value: patsnap_category, isLeft: true },
                { key: 'category', label: this.i18n.t('backoffice.company.category'), value: info?.category, isLeft: true },
                { key: 'innovation_categories', label: this.i18n.t('backoffice.company.innovation_categories'), value: info?.innovation_categories, isLeft: true },
                { key: 'company_size', label: this.i18n.t('backoffice.company.company_size'), value: info?.company_size, isLeft: true },
                { key: 'qualifications', label: this.i18n.t('backoffice.company.qualifications'), value: info?.qualifications },
                { key: 'tags', label: this.i18n.t('backoffice.company.tags'), value: info?.tags },
                { key: 'industry', label: this.i18n.t('backoffice.company.industry'), value: info?.industry }
            ]
            : [
                { key: 'linkedin_industry', label: this.i18n.t('backoffice.globalView.search.linkedinIndustry'), value: companyInfo?.linkedin_industry },
                { key: 'patsnap_vertical', label: this.i18n.t('backoffice.globalView.search.verticals'), value: companyInfo?.patsnap_vertical, isLeft: true },
                { key: 'account_tier', label: this.i18n.t('backoffice.company.account_tier'), value: salesforceInfo.account_tier, isLeft: true },
                { key: 'company_size', label: this.i18n.t('backoffice.company.company_size'), value: info?.company_size, isLeft: true },
                { key: 'company_rank', label: this.i18n.t('backoffice.globalView.search.company_rank'), value: salesforceInfo.company_rank, isLeft: true }
            ];
    }
    /**
     * 获取公司系统信息
     */
    getSystemInfo(companyInfo) {
        const info = companyInfo?.organization_info;
        const is_trial_self_register = info?.self_trial ? this.i18n.t('backoffice.common.yes') : this.i18n.t('backoffice.common.no');
        const createdFormI18nKey = `backoffice.enum.E_UserInfoCreatedFrom.${info?.created_from_value}`;
        const createdFrom = this.i18n.t(createdFormI18nKey) !== createdFormI18nKey ? this.i18n.t(createdFormI18nKey) : info?.created_from_value;
        const isEast = companyInfo.area === 'east';
        return [
            { key: 'created_from', label: this.i18n.t('backoffice.account.creat_from'), value: createdFrom },
            { key: 'is_trial_self_register', label: this.i18n.t('backoffice.company.fromTrialApplication'), value: is_trial_self_register },
            { key: 'created_by', label: this.i18n.t('coreUtility.account.created_by'), value: companyInfo?.created_by_email },
            { key: 'created_at', label: this.i18n.t('backoffice.account.createdAt'), value: getDateFormatYMDHMS(companyInfo?.created_at, this.i18n.locale) },
            { key: 'id', label: this.i18n.t('backoffice.abtest.companyId'), value: companyInfo?.id },
            ...isEast
                ? [{ key: 'xiaoshouyi_id', label: this.i18n.t('backoffice.common.xiaoshouyiId'), value: companyInfo?.xiaoshouyi_id }]
                : [{ key: 'salesforce_id', label: this.i18n.t('backoffice.common.salesforceId'), value: companyInfo?.salesforce_id }]
        ];
    }
    /**
     * 获取公司专利信息
     */
    getPatentInfo(data) {
        const company_tffi = data?.company_tffi;
        return [
            { key: 'total_apno_cnt', label: this.i18n.t('backoffice.company.total_patents'), value: company_tffi?.total_apno_cnt, cols: '4' },
            { key: 'app_cnt_3y', label: this.i18n.t('backoffice.company.applied_in3_years_patents'), value: company_tffi?.app_cnt_3y, cols: '4' },
            { key: 'pub_cnt_3y', label: this.i18n.t('backoffice.company.public_in3_years_patents'), value: company_tffi?.pub_cnt_3y, cols: '4' },
            { key: 'granted_apno_cnt', label: this.i18n.t('backoffice.company.valid_patents'), value: company_tffi?.granted_apno_cnt, cols: '3' },
            { key: 'total_invention_apno_cnt', label: this.i18n.t('backoffice.company.invent_patent'), value: company_tffi?.total_invention_apno_cnt, cols: '3' },
            { key: 'total_utility_apno_cnt', label: this.i18n.t('backoffice.company.utility_patents'), value: company_tffi?.total_utility_apno_cnt, cols: '3' },
            { key: 'total_design_apno_cnt', label: this.i18n.t('backoffice.company.design_patents'), value: company_tffi?.total_design_apno_cnt, cols: '3' }
        ];
    }
    /**
     * 当年ACV
     */
    getCurYearAcv(data) {
        const company_finance = data?.company_finance;
        return this.customerService.transformAcv(company_finance?.currency, company_finance?.cur_year_acv);
    }
    /**
     * 最近一次融资时间轮次信息
     */
    getLastFinancingRoundInfo(data) {
        const company_tffi = data?.company_tffi;
        const lastFinancingRound = this.i18n.locale === 'cn' ? company_tffi?.last_financing_round : company_tffi?.last_financing_round_en;
        if (!lastFinancingRound) {
            return '-';
        }
        const date = getDateFormatYMD(new Date(company_tffi.last_financing_announce_date), this.i18n.locale);
        return this.i18n.t('backoffice.company.customer360.lastFinancingRoundInfo', { round: lastFinancingRound, date: date });
    }
    /**
     * 获取财务信息
     */
    getFinancialInfo(data, companyInfo) {
        const company_finance = data?.company_finance;
        const company_tffi = data?.company_tffi;
        const service_level = companyInfo?.service_level || '-';
        const funding_amount = (company_tffi?.financing_currency || '') + ' ' + (this.formatValue(company_tffi?.money_raised_upper || company_tffi?.money_raised_lower));
        const lastFinancingRoundInfo = this.getLastFinancingRoundInfo(data);
        return [
            { key: 'cur_year_acv', label: this.i18n.t('backoffice.company.cur_year_acv'), value: this.getCurYearAcv(data) },
            { key: 'arr', label: this.i18n.t('backoffice.company.cur_year_arr'), value: this.customerService.transformAcv(company_finance?.currency, company_finance?.arr, E_AcvAndArr.ARR) },
            { key: 'service_level', label: this.i18n.t('backoffice.company.levelOfService'), value: service_level },
            { key: 'funding_amount', label: this.i18n.t('backoffice.company.customer360.latestFundingAmount'), value: funding_amount, desc: lastFinancingRoundInfo }
        ];
    }
    formatValue(value) {
        if (_.isArray(value)) {
            if (_.isEmpty(value)) {
                return '-';
            }
            return _.join(value, this.i18n.t('backoffice.common.comma'));
        }
        if (_.isNumber(value)) {
            return _.toNumber(value).toLocaleString();
        }
        if (_.isNil(value) || _.isEmpty(value)) {
            return '-';
        }
        return value;
    }
};
CompanyInfoService = __decorate([
    injectable(),
    __param(0, inject(Symbols.I18n)),
    __param(1, inject(Symbols.CustomerService)),
    __param(2, inject(Symbols.SystemService)),
    __metadata("design:paramtypes", [Object, Object, Object])
], CompanyInfoService);
export { CompanyInfoService };
